import React, { useState, useEffect, useCallback, useRef, memo, useMemo } from 'react';
import { I18n } from 'core';

// Direct imports - avoid barrel file 'components'
import EnergyWidget from 'components/widgets/EnergyWidget/EnergyWidget';
import SavingWidget from 'components/widgets/SavingWidget/SavingWidget';
import SustainabilityWidget from 'components/widgets/SustainabilityWidget/SustainabilityWidget';
import AmortizationWidget from 'components/widgets/AmortizationWidget/AmortizationWidget';
import SharedEnergyWidget from 'components/widgets/SharedEnergyWidget/SharedEnergyWidget';
import TabsSwitch from 'components/TabsSwitch/TabsSwitch';
import ContractSummaryWidget from 'components/widgets/ContractSummaryWidget/ContractSummaryWidget';
import Spinner from 'components/Spinner';
import EnergyFotovoltaicCard from 'components/widgets/EnergyFotovoltaicWidget/EnergyFotovoltaicWidget';
import SavingAgregateCardCustom from 'components/widgets/SavingAgregateWidget/SavingAgregateWidgetCustom';
import Slider from 'components/SliderHorizontal/Slider';

import { clientServiceInstance, CupsInfoValues } from 'services';
import { EnergyObject, SavingObject, SustainabilityObject, CupsInfo, Client } from 'types/models';
import AmortizationObject from 'types/models/widgetsData/amortizationObj';
import SharedEnergyObject from 'types/models/widgetsData/sharedEnergyObj';
import ContractSummary from 'types/models/widgetsData/contractSummaryObj';
import { navigate } from 'navigation';
import { useSelector } from 'react-redux';

import { GlobalStoreState } from 'types/state';
import { toast } from 'react-toastify';

import cx from 'classnames';
import { pickAccumulated, round } from 'services/local/number';
import Images from './images';

import styles from './HomePage.module.scss';

// --- Types ---

interface LoadingState {
  energy: boolean;
  saving: boolean;
  amortization: boolean;
  sustainability: boolean;
  sharedEnergy: boolean;
  contractSummary: boolean;
  tarifa: boolean;
}

const INITIAL_LOADING: LoadingState = {
  energy: false,
  saving: false,
  amortization: false,
  sustainability: false,
  sharedEnergy: false,
  contractSummary: false,
  tarifa: false,
};

const TABS_AVAILABLE = [
  { key: CupsInfoValues.MINE, label: I18n.t('La meva') },
  { key: CupsInfoValues.COMMUNITY, label: I18n.t('Comunitat') },
];

// --- Widget wrapper ---

interface WidgetSlotProps {
  loading: boolean;
  className: string;
  children: React.ReactNode | null;
}

const WidgetSlot = ({ loading: isLoading, className, children }: WidgetSlotProps): JSX.Element | null => {
  if (isLoading) {
    return (
      <div className={className}>
        <Spinner icon />
      </div>
    );
  }
  return children ? <>{children}</> : null;
};

// --- Extracted Views ---

interface ViewProps {
  cupsID: string;
  cupsSelected: CupsInfo | null;
  adminCups: Client | null;
  loading: LoadingState;
  energyData: EnergyObject | null;
  savingData: SavingObject | null;
  amortizationData: AmortizationObject | null;
  sustainabilityData: SustainabilityObject | null | undefined;
  sharedEnergyData: SharedEnergyObject | null | undefined;
  contractSummaryData: ContractSummary | null | undefined;
  Tarifa: any;
  showSustainability: boolean;
  lenguageDefault: boolean;
  fechaMesAñoArrayPositionDad: number;
  fechaMesAñoArrayPositionDadIndividual: number;
  onDateChange: (date: Date, pos: number) => void;
  onDateChangeIndividual: (date: Date, pos: number) => void;
}

const CommunityView = memo(
  ({
    cupsID,
    cupsSelected,
    loading,
    energyData,
    savingData,
    amortizationData,
    sustainabilityData,
    sharedEnergyData,
    showSustainability,
    fechaMesAñoArrayPositionDad,
    onDateChange,
  }: ViewProps): JSX.Element => (
    <div className={styles.container}>
      <WidgetSlot loading={loading.energy} className={styles.energy_widget}>
        {energyData && energyData.showWidget ? (
          <div className={cx(styles.energy_widget, { [styles.expand]: cupsSelected?.fotovoltaica === 'si' })}>
            <EnergyWidget
              fotovoltaic={cupsSelected?.fotovoltaica || 'no'}
              date={energyData.comunity.date}
              generationData={energyData.comunity.generation}
              shrinkData={energyData.comunity.shrink}
              networkData={energyData.comunity.network}
              selfConsumptionData={energyData.comunity.selfconsumption}
              consumptionData={energyData.comunity.consumption}
            />
          </div>
        ) : null}
      </WidgetSlot>

      <WidgetSlot loading={loading.energy} className={styles.energy_fv_widget}>
        {energyData && energyData.showWidget && cupsSelected?.fotovoltaica === 'si' ? (
          <div className={styles.energy_fv_widget}>
            <EnergyFotovoltaicCard
              date={energyData.comunity.date}
              generationData={energyData.comunity.generation}
              shrinkData={energyData.comunity.shrink}
              networkData={energyData.comunity.network}
              selfConsumptionData={energyData.comunity.selfconsumption}
              consumptionData={energyData.comunity.consumption}
            />
          </div>
        ) : null}
      </WidgetSlot>

      <WidgetSlot loading={loading.saving} className={styles.saving_widget}>
        {savingData && savingData.showWidget && savingData.comunity.ahorrosPorMes.length > 0 ? (
          <div className={styles.saving_widget}>
            <SavingWidget
              isClient={savingData.isClient}
              comercializado={savingData.comercializado}
              date={savingData.comunity.ahorrosPorMes[fechaMesAñoArrayPositionDad].fecha}
              savingData={savingData.comunity.ahorrosPorMes[fechaMesAñoArrayPositionDad].ahorro}
              costWithoutElecsumData={savingData.comunity.ahorrosPorMes[fechaMesAñoArrayPositionDad].importeSinElecsum}
              shrinkingData={savingData.comunity.ahorrosPorMes[fechaMesAñoArrayPositionDad].excedente}
              selfConsumptionData={savingData.comunity.ahorrosPorMes[fechaMesAñoArrayPositionDad].autoconsumida}
              acumulatedSaving={savingData.comunity.ahorrosPorMes[fechaMesAñoArrayPositionDad].ahorroAcumulado}
              costWithElecsumData={savingData.comunity.ahorrosPorMes[fechaMesAñoArrayPositionDad].importeElecsum}
              startDate={savingData.comunity.ahorroAcumuladoInicio}
              finalDate={savingData.comunity.ahorroAcumuladoFin}
              onDateChange={onDateChange}
              maxArrayPosition={savingData.comunity.ahorrosPorMes.length - 1}
              fechaMesAñoArrayPositionDad={fechaMesAñoArrayPositionDad}
            />
          </div>
        ) : null}
      </WidgetSlot>

      <WidgetSlot loading={loading.saving} className={styles.saving_agregate_widget}>
        <div className={styles.saving_agregate_widget}>
          <SavingAgregateCardCustom
            acumulatedSaving={
              savingData &&
              savingData.comunity &&
              savingData.comunity.ahorrosPorMes &&
              savingData.comunity.ahorrosPorMes.length > 0
                ? pickAccumulated(
                    savingData.comunity.ahorrosPorMes[savingData.comunity.ahorrosPorMes.length - 1].ahorroAcumulado,
                    amortizationData?.individual?.amortized,
                  )
                : 0
            }
            startDate={
              savingData &&
              savingData.comunity &&
              savingData.comunity.ahorrosPorMes &&
              savingData.comunity.ahorrosPorMes.length > 0
                ? savingData.comunity.ahorroAcumuladoInicio
                : `${new Date().getFullYear()}-${String(new Date().getMonth() + 1).padStart(2, '0')}`
            }
          />
        </div>
      </WidgetSlot>

      {showSustainability ? (
        <WidgetSlot loading={loading.sustainability} className={styles.sustainability_widget}>
          {sustainabilityData ? (
            <div className={styles.sustainability_widget}>
              <SustainabilityWidget
                cups={cupsID}
                title="sustainability"
                subtitle={I18n.t('totalAccumulated')}
                noEmmitedData={sustainabilityData.comunity.co2}
                noEmmitedUnit="kg CO2"
                treesData={sustainabilityData.comunity.trees}
                viewMore
              />
            </div>
          ) : null}
        </WidgetSlot>
      ) : null}

      <WidgetSlot loading={loading.sharedEnergy} className={styles.shared_widget}>
        {sharedEnergyData && sharedEnergyData.showWidget ? (
          <div className={styles.shared_widget}>
            <SharedEnergyWidget
              powerInstallation={sharedEnergyData.powerInstallation}
              totalGeneration={sharedEnergyData.totalGeneration}
              members={sharedEnergyData.members}
            />
          </div>
        ) : null}
      </WidgetSlot>
    </div>
  ),
);

const MineView = memo(
  ({
    cupsID,
    cupsSelected,
    adminCups,
    loading,
    energyData,
    savingData,
    amortizationData,
    sustainabilityData,
    contractSummaryData,
    Tarifa,
    showSustainability,
    lenguageDefault,
    fechaMesAñoArrayPositionDadIndividual,
    onDateChangeIndividual,
  }: ViewProps): JSX.Element => (
    <div className={styles.container}>
      <WidgetSlot loading={loading.energy} className={styles.energy_widget}>
        {energyData && cupsSelected && energyData.showWidget ? (
          <div className={cx(styles.energy_widget, { [styles.expand]: cupsSelected?.fotovoltaica === 'si' })}>
            <EnergyWidget
              fotovoltaic={cupsSelected.fotovoltaica || 'no'}
              date={energyData.individual.date}
              generationData={energyData.individual.generation}
              shrinkData={energyData.individual.shrink}
              networkData={energyData.individual.network}
              selfConsumptionData={energyData.individual.selfconsumption}
              consumptionData={energyData.individual.consumption}
            />
          </div>
        ) : null}
      </WidgetSlot>

      <WidgetSlot loading={loading.energy} className={styles.energy_fv_widget}>
        {cupsSelected && cupsSelected.fotovoltaica === 'si' && energyData && energyData.showWidget ? (
          <div className={styles.energy_fv_widget}>
            <EnergyFotovoltaicCard
              date={energyData.individual.date}
              generationData={energyData.individual.generation}
              shrinkData={energyData.individual.shrink}
              networkData={energyData.individual.network}
              selfConsumptionData={energyData.individual.selfconsumption}
              consumptionData={energyData.individual.consumption}
            />
          </div>
        ) : null}
      </WidgetSlot>

      <WidgetSlot loading={loading.saving} className={styles.saving_widget}>
        {savingData && savingData.showWidget && savingData.individual.ahorrosPorMes.length > 0 ? (
          <div className={styles.saving_widget}>
            <SavingWidget
              comercializado={savingData.comercializado}
              isClient={savingData.isClient}
              date={savingData.individual.ahorrosPorMes[fechaMesAñoArrayPositionDadIndividual].fecha}
              savingData={savingData.individual.ahorrosPorMes[fechaMesAñoArrayPositionDadIndividual].ahorro}
              costWithoutElecsumData={
                savingData.individual.ahorrosPorMes[fechaMesAñoArrayPositionDadIndividual].importeSinElecsum
              }
              shrinkingData={savingData.individual.ahorrosPorMes[fechaMesAñoArrayPositionDadIndividual].excedente}
              selfConsumptionData={
                savingData.individual.ahorrosPorMes[fechaMesAñoArrayPositionDadIndividual].autoconsumida
              }
              costWithElecsumData={
                savingData.individual.ahorrosPorMes[fechaMesAñoArrayPositionDadIndividual].importeElecsum
              }
              acumulatedSaving={
                savingData.individual.ahorrosPorMes[fechaMesAñoArrayPositionDadIndividual].ahorroAcumulado
              }
              startDate={savingData.individual.ahorroAcumuladoInicio}
              finalDate={savingData.individual.ahorroAcumuladoFin}
              onDateChange={onDateChangeIndividual}
              maxArrayPosition={savingData.individual.ahorrosPorMes.length - 1}
              fechaMesAñoArrayPositionDad={fechaMesAñoArrayPositionDadIndividual}
            />
          </div>
        ) : null}
      </WidgetSlot>

      <WidgetSlot loading={loading.saving} className={styles.saving_agregate_widget}>
        {(() => {
          let acumulatedSaving = 0;
          let startDate = '';
          if (
            savingData &&
            savingData.individual &&
            savingData.individual.ahorrosPorMes &&
            savingData.individual.ahorrosPorMes.length > 0
          ) {
            const lastValue = savingData.individual.ahorrosPorMes[savingData.individual.ahorrosPorMes.length - 1];
            acumulatedSaving = pickAccumulated(lastValue.ahorroAcumulado, amortizationData?.individual?.amortized);
            startDate = savingData.individual.ahorroAcumuladoInicio;
          } else {
            const date = new Date();
            startDate = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
          }
          return (
            <div className={styles.saving_agregate_widget}>
              <SavingAgregateCardCustom acumulatedSaving={acumulatedSaving} startDate={startDate} />
            </div>
          );
        })()}
      </WidgetSlot>

      <WidgetSlot loading={loading.amortization} className={styles.amortization_widget}>
        {amortizationData && amortizationData.showWidget ? (
          <div className={styles.amortization_widget}>
            <AmortizationWidget
              totalData={amortizationData.individual.total}
              amortizedData={round(+amortizationData.individual.amortized, 2)}
              percentAmortized={amortizationData.individual.amortizedPercentage}
              duration={amortizationData.duration}
            />
          </div>
        ) : null}
      </WidgetSlot>

      {showSustainability ? (
        <WidgetSlot loading={loading.sustainability} className={styles.sustainability_widget}>
          {!adminCups?.nif && sustainabilityData ? (
            <div className={styles.sustainability_widget}>
              <SustainabilityWidget
                cups={cupsID}
                title="sustainability"
                subtitle={I18n.t('totalAccumulated')}
                noEmmitedData={sustainabilityData.individual.co2}
                noEmmitedUnit="kg CO2"
                treesData={sustainabilityData.individual.trees}
                viewMore
              />
            </div>
          ) : null}
        </WidgetSlot>
      ) : null}

      <WidgetSlot loading={loading.tarifa} className={styles.sustainability_widget}>
        {Tarifa && Tarifa.tarifaComercializadora ? (
          <div className={styles.energy_fv_widget}>
            {Tarifa.tarifaComercializadora === 'FIX' ? (
              <Slider
                images={lenguageDefault ? Images.plaFlix.es : Images.plaFlix.cat}
                Titulo={Tarifa.tarifaComercializadora}
              />
            ) : null}
            {Tarifa.tarifaComercializadora === 'FLEXA2' ? (
              <Slider
                images={lenguageDefault ? Images.plaFlexa2.es : Images.plaFlexa2.cat}
                Titulo={Tarifa.tarifaComercializadora}
              />
            ) : null}
            {Tarifa.tarifaComercializadora === 'FLEXA3' ? (
              <Slider
                images={lenguageDefault ? Images.platFlexa3.es : Images.platFlexa3.cat}
                Titulo={Tarifa.tarifaComercializadora}
              />
            ) : null}
          </div>
        ) : null}
      </WidgetSlot>

      <WidgetSlot loading={loading.contractSummary} className={styles.summary_widget}>
        {contractSummaryData ? (
          <div
            className={cx(
              styles.summary_widget,
              {
                [styles.expand_mid]: contractSummaryData.instalaciones
                  ? contractSummaryData.instalaciones.length > 0
                  : false,
              },
              {
                [styles.expand_big]:
                  [
                    contractSummaryData.potencias.potencia1 && 1,
                    contractSummaryData.potencias.potencia2 && 1,
                    contractSummaryData.potencias.potencia3 && 1,
                    contractSummaryData.potencias.potencia4 && 1,
                    contractSummaryData.potencias.potencia5 && 1,
                    contractSummaryData.potencias.potencia6 && 1,
                  ].reduce((a, b) => a + b, 0) > 2 && contractSummaryData.instalaciones?.length === 0,
              },
              {
                [styles.expand_extra]:
                  [
                    contractSummaryData.potencias.potencia1 && 1,
                    contractSummaryData.potencias.potencia2 && 1,
                    contractSummaryData.potencias.potencia3 && 1,
                    contractSummaryData.potencias.potencia4 && 1,
                    contractSummaryData.potencias.potencia5 && 1,
                    contractSummaryData.potencias.potencia6 && 1,
                  ].reduce((a, b) => a + b, 0) > 3,
              },
            )}
          >
            <ContractSummaryWidget
              instalaciones={contractSummaryData.instalaciones || []}
              codigoCliente={contractSummaryData.codigoCliente}
              tarifa={contractSummaryData.tarifa}
              tarifaComercializadora={contractSummaryData.tarifaComercializadora}
              porcentajeExencionImpuesto={contractSummaryData.porcentajeExencionImpuesto}
              porcentajeElectricidadBonificacion={contractSummaryData.porcentajeElectricidadBonificacion}
              p1={contractSummaryData.potencias.potencia1}
              p2={contractSummaryData.potencias.potencia2}
              p3={contractSummaryData.potencias.potencia3}
              p4={contractSummaryData.potencias.potencia4}
              p5={contractSummaryData.potencias.potencia5}
              p6={contractSummaryData.potencias.potencia6}
              eolica={contractSummaryData.tecnologia.eolica === 'si'}
              fotovoltaica={contractSummaryData.tecnologia.fotovoltaica === 'si'}
              comercializado={contractSummaryData.comercializado === 'si'}
            />
          </div>
        ) : null}
      </WidgetSlot>
    </div>
  ),
);

// --- Main Component ---

const HomePage = (props: any): JSX.Element => {
  const {
    match: {
      params: { cupsID },
    },
  } = props;

  const cupsSelected = useSelector((state: GlobalStoreState) => state.clientCUPS.selectedCups);
  const adminCups = useSelector((state: GlobalStoreState) => state.cups.selectedCups);
  const estado = useSelector((state: GlobalStoreState) => state.cups.selectedCupsStatus);
  const selectedMulticau = useSelector((state: GlobalStoreState) => state.clientCUPS.selectedMulticau);

  const [dataLoaded, setDataLoaded] = useState(false);
  const [loading, setLoading] = useState<LoadingState>(INITIAL_LOADING);

  const [energyData, setEnergyData] = useState<EnergyObject | null>(null);
  const [savingData, setSavingData] = useState<SavingObject | null>(null);
  const [amortizationData, setAmortizationData] = useState<AmortizationObject | null>(null);
  const [sustainabilityData, setSustainabilityData] = useState<SustainabilityObject | null>();
  const [sharedEnergyData, setSharedEnergyData] = useState<SharedEnergyObject | null>();
  const [contractSummaryData, setContractSummaryData] = useState<ContractSummary | null>();
  const [Tarifa, setAlltarifa] = useState<any>();

  const [tabSelected, setTabSelected] = useState(CupsInfoValues.MINE);
  const [fechaMesAñoArrayPositionDad, setFechaMesAñoArrayPositionDad] = useState(0);
  const [fechaMesAñoArrayPositionDadIndividual, setFechaMesAñoArrayPositionDadIndividual] = useState(0);

  const lenguageDefault = useMemo(() => navigator.language === 'es', []);

  const lastFetchRef = useRef('');
  const dataCacheRef = useRef<
    Map<
      string,
      {
        energy: EnergyObject | null;
        saving: SavingObject | null;
        amortization: AmortizationObject | null;
        sharedEnergy: SharedEnergyObject | null | undefined;
        contractSummary: ContractSummary | null | undefined;
      }
    >
  >(new Map());

  // Redirigir si CUPS no coincide con URL
  useEffect(() => {
    if (cupsSelected && cupsSelected.cups !== cupsID) {
      navigate(`/client/${cupsSelected.cups}/home`);
    }
  }, [cupsSelected]);

  // Limpiar caché cuando cambia el CUPS (nueva instalación)
  const prevCupsRef = useRef(cupsSelected?.cups);
  useEffect(() => {
    if (cupsSelected && prevCupsRef.current !== cupsSelected.cups) {
      dataCacheRef.current.clear();
      lastFetchRef.current = '';
      prevCupsRef.current = cupsSelected.cups;
    }
  }, [cupsSelected]);

  const filterAhorros = useCallback(
    (ahorros: any[]) =>
      ahorros.filter(
        (item) =>
          item.fecha !== '' ||
          item.ahorro !== 0 ||
          item.importeSinElecsum !== 0 ||
          item.excedente !== 0 ||
          item.autoconsumida !== 0 ||
          item.importeElecsum !== 0 ||
          item.ahorroAcumulado !== 0,
      ),
    [],
  );

  // Fetch data que depende de codiClient (con caché en memoria)
  useEffect(() => {
    if (!cupsSelected) return;

    const codiClient = selectedMulticau?.codiClient || undefined;
    const multicauKey = selectedMulticau ? selectedMulticau.codiClient || 'todos' : '';
    const { cups } = cupsSelected;

    const fetchKey = `${cups}_${multicauKey}`;
    if (lastFetchRef.current === fetchKey) return;
    lastFetchRef.current = fetchKey;

    // Comprobar caché antes de hacer llamadas API
    const cached = dataCacheRef.current.get(fetchKey);
    if (cached) {
      setEnergyData(cached.energy);
      setSavingData(cached.saving);
      setAmortizationData(cached.amortization);
      setSharedEnergyData(cached.sharedEnergy);
      setContractSummaryData(cached.contractSummary);
      setDataLoaded(true);
      return;
    }

    setLoading((prev) => ({
      ...prev,
      energy: true,
      saving: true,
      amortization: true,
      sharedEnergy: true,
      contractSummary: true,
    }));
    setDataLoaded(true);

    const cacheEntry: Record<string, any> = {};
    const saveCacheIfComplete = () => {
      if (
        'energy' in cacheEntry &&
        'saving' in cacheEntry &&
        'amortization' in cacheEntry &&
        'sharedEnergy' in cacheEntry &&
        'contractSummary' in cacheEntry
      ) {
        dataCacheRef.current.set(fetchKey, cacheEntry as any);
      }
    };

    clientServiceInstance
      .getEnergyData(cups, codiClient)
      .then((response) => {
        setEnergyData(response);
        cacheEntry.energy = response;
      })
      .catch(() => {
        toast.error(I18n.t('getEnergyDataError'));
        cacheEntry.energy = null;
      })
      .finally(() => {
        setLoading((prev) => ({ ...prev, energy: false }));
        saveCacheIfComplete();
      });

    const savingCodiClient = codiClient || (selectedMulticau ? 'todos' : undefined);
    clientServiceInstance
      .getSavingData(cups, savingCodiClient)
      .then((response) => {
        response.individual.ahorrosPorMes = filterAhorros(response.individual.ahorrosPorMes);
        response.comunity.ahorrosPorMes = filterAhorros(response.comunity.ahorrosPorMes);
        setSavingData(response);
        cacheEntry.saving = response;
      })
      .catch(() => {
        toast.error(I18n.t('getSavingDataError'));
        cacheEntry.saving = null;
      })
      .finally(() => {
        setLoading((prev) => ({ ...prev, saving: false }));
        saveCacheIfComplete();
      });

    const amortizationCodiClient = codiClient || (selectedMulticau ? 'todos' : undefined);
    clientServiceInstance
      .getAmortizationData(cups, amortizationCodiClient)
      .then((response) => {
        setAmortizationData(response);
        cacheEntry.amortization = response;
      })
      .catch(() => {
        toast.error(I18n.t('getAmortizationDataError'));
        cacheEntry.amortization = null;
      })
      .finally(() => {
        setLoading((prev) => ({ ...prev, amortization: false }));
        saveCacheIfComplete();
      });

    clientServiceInstance
      .getSharedEnergyData(cups, codiClient)
      .then((response) => {
        setSharedEnergyData(response);
        cacheEntry.sharedEnergy = response;
      })
      .catch(() => {
        toast.error(I18n.t('getSharedEnergyDataError'));
        cacheEntry.sharedEnergy = null;
      })
      .finally(() => {
        setLoading((prev) => ({ ...prev, sharedEnergy: false }));
        saveCacheIfComplete();
      });

    const contractSummaryCodiClient = codiClient || (selectedMulticau ? 'todos' : undefined);
    clientServiceInstance
      .getContractSummary(cups, contractSummaryCodiClient)
      .then((response) => {
        setContractSummaryData(response);
        cacheEntry.contractSummary = response;
      })
      .catch(() => {
        toast.error(I18n.t('getSummaryDataError'));
        cacheEntry.contractSummary = null;
      })
      .finally(() => {
        setLoading((prev) => ({ ...prev, contractSummary: false }));
        saveCacheIfComplete();
      });
  }, [cupsSelected, selectedMulticau, filterAhorros]);

  // Fetch data que NO depende de codiClient
  useEffect(() => {
    if (!cupsSelected) return;

    if (estado !== 'STATUS_OK') {
      setLoading((prev) => ({ ...prev, sustainability: true }));
      clientServiceInstance
        .getSustainabilityData(cupsSelected.cups)
        .then((response) => setSustainabilityData(response))
        .catch(() => toast.error(I18n.t('getSustainabilityDataError')))
        .finally(() => setLoading((prev) => ({ ...prev, sustainability: false })));
    }

    setLoading((prev) => ({ ...prev, tarifa: true }));
    clientServiceInstance
      .getClient(cupsSelected.cups)
      .then((response) => setAlltarifa(response))
      .catch(() => toast.error(I18n.t('getEnergyDataError')))
      .finally(() => setLoading((prev) => ({ ...prev, tarifa: false })));
  }, [cupsSelected]);

  // Forzar a "La Meva" si multicau "Todas"
  useEffect(() => {
    if (selectedMulticau && !selectedMulticau.codiClient && tabSelected === CupsInfoValues.COMMUNITY) {
      setTabSelected(CupsInfoValues.MINE);
    }
  }, [selectedMulticau]);

  // Posición del slider de ahorro
  useEffect(() => {
    if (savingData?.comunity?.ahorrosPorMes?.length) {
      setFechaMesAñoArrayPositionDad(savingData.comunity.ahorrosPorMes.length - 1);
    }
    if (savingData?.individual?.ahorrosPorMes?.length) {
      setFechaMesAñoArrayPositionDadIndividual(savingData.individual.ahorrosPorMes.length - 1);
    }
  }, [savingData]);

  const handleTabClick = useCallback((option: any) => setTabSelected(option.key), []);
  const handleDateChange = useCallback((_: Date, pos: number) => setFechaMesAñoArrayPositionDad(pos), []);
  const handleDateChangeIndividual = useCallback(
    (_: Date, pos: number) => setFechaMesAñoArrayPositionDadIndividual(pos),
    [],
  );

  const showSustainability = !selectedMulticau || !!selectedMulticau.codiClient;
  const isComunidad =
    selectedMulticau && selectedMulticau.codiClient
      ? selectedMulticau.esComunidad === 'si'
      : cupsSelected?.esComunidad === 'si' ||
        (cupsSelected?.detallesCupsMulticau || []).some((d) => d.esComunidad === 'si');
  const tabOptions = useMemo(() => {
    const hasAnyCommunity =
      cupsSelected?.esComunidad === 'si' ||
      (cupsSelected?.detallesCupsMulticau || []).some((d) => d.esComunidad === 'si');
    if (selectedMulticau && !selectedMulticau.codiClient && !hasAnyCommunity) {
      return TABS_AVAILABLE.filter((tab) => tab.key !== CupsInfoValues.COMMUNITY);
    }
    return TABS_AVAILABLE;
  }, [selectedMulticau, cupsSelected]);
  const isTodosView = !!selectedMulticau && !selectedMulticau.codiClient;

  // Clamp positions so a stale index never out-runs a freshly-loaded ahorrosPorMes array.
  const comunityLen = savingData?.comunity?.ahorrosPorMes?.length ?? 0;
  const individualLen = savingData?.individual?.ahorrosPorMes?.length ?? 0;
  const safeFechaMesAñoArrayPositionDad = comunityLen > 0 ? Math.min(fechaMesAñoArrayPositionDad, comunityLen - 1) : 0;
  const safeFechaMesAñoArrayPositionDadIndividual =
    individualLen > 0 ? Math.min(fechaMesAñoArrayPositionDadIndividual, individualLen - 1) : 0;

  const viewProps: ViewProps = {
    cupsID,
    cupsSelected,
    adminCups,
    loading,
    energyData,
    savingData,
    amortizationData,
    sustainabilityData,
    sharedEnergyData,
    contractSummaryData,
    Tarifa,
    showSustainability,
    lenguageDefault,
    fechaMesAñoArrayPositionDad: safeFechaMesAñoArrayPositionDad,
    fechaMesAñoArrayPositionDadIndividual: safeFechaMesAñoArrayPositionDadIndividual,
    onDateChange: handleDateChange,
    onDateChangeIndividual: handleDateChangeIndividual,
  };

  return (
    <div>
      {dataLoaded ? (
        <div>
          {isComunidad && !isTodosView ? (
            <TabsSwitch options={tabOptions} selected={tabSelected} onTabClick={handleTabClick} />
          ) : null}
          {tabSelected === CupsInfoValues.MINE ? <MineView {...viewProps} /> : null}
          {tabSelected === CupsInfoValues.COMMUNITY && isComunidad && !isTodosView ? (
            <CommunityView {...viewProps} />
          ) : null}
        </div>
      ) : (
        <div className={styles.spinnerGeneric}>
          <Spinner icon />
        </div>
      )}
    </div>
  );
};

export default HomePage;
